import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Layout } from "../components/layout";
import { SEO } from "../components/SEO";
import { useCMSPage } from "../hooks/useCMSPage";
import { sanitizeHTML } from "../utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  hero_label: "About Us",
  hero_title: "Vibe Coding for",
  hero_title_accent: "Real Results",
  hero_subtitle: "It's IT is a solo-entrepreneur LLC dedicated to creating beautiful, functional websites for small businesses and non-profits.",
  story_title: "Our Story",
  story_content: "<p>Cabernet sauvignon shiraz pinot noir merlot, vintage tannins nose chardonnay. Oak-aged complexity with balanced fruit notes and an elegant finish that lingers on the palate.</p><p>Zinfandel varietals sommelier decanting, terroir expression with subtle minerality. The vineyard's unique microclimate contributes to the wine's distinctive character.</p><p>Malbec cuvée grand cru appellation, bouquet rosé champagne prosecco. The vintage shows remarkable depth and character, with layers of complexity that unfold with each sip.</p>",
  stat_1_value: "50+",
  stat_1_label: "Projects Completed",
  stat_2_value: "100%",
  stat_2_label: "Client Satisfaction",
  value_1_title: "Quality First",
  value_1_description: "Every line of code, every design decision is made with quality in mind.",
  value_2_title: "Client Partnership",
  value_2_description: "We work alongside you, not just for you. Your success is our success.",
  value_3_title: "Innovation",
  value_3_description: "Staying current with the latest technologies to deliver modern solutions.",
};

export const AboutPage = () => {
  const [navItems, setNavItems] = useState([]);
  const { fields: f, content } = useCMSPage("about", DEFAULTS);

  useEffect(() => {
    axios.get(`${API}/navigation`).then((res) => setNavItems(res.data)).catch(console.error);
  }, []);

  return (
    <Layout navItems={navItems}>
      <SEO
        title="About Us"
        description="Learn about It's IT, a solo-entrepreneur LLC dedicated to creating beautiful, functional websites for small businesses and non-profits."
        keywords="about It's IT, web development company, small business web design, Arizona web developer"
      />
      {/* Hero */}
      <section data-testid="about-hero" className="section-padding bg-its-teal-dark">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="label-text">{f.hero_label}</span>
            <h1 className="heading-1 mt-2 mb-6">
              {f.hero_title}
              <span className="text-its-gold"> {f.hero_title_accent}</span>
            </h1>
            <p className="body-text">
              {f.hero_subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Content */}
      <section className="section-padding">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="heading-3 mb-6">{f.story_title}</h2>
            <div className="space-y-4 text-its-offwhite/90 prose-content" dangerouslySetInnerHTML={{ __html: sanitizeHTML(f.story_content) }} />

            <div className="mt-8 grid grid-cols-2 gap-6">
              <div className="glass-card text-center">
                <div className="text-3xl font-bold text-its-gold mb-2">{f.stat_1_value}</div>
                <div className="text-sm text-its-offwhite/80">{f.stat_1_label}</div>
              </div>
              <div className="glass-card text-center">
                <div className="text-3xl font-bold text-its-gold mb-2">{f.stat_2_value}</div>
                <div className="text-sm text-its-offwhite/80">{f.stat_2_label}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-its-teal-dark">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="label-text">Our Values</span>
            <h2 className="heading-2 mt-2">What Drives Us</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: f.value_1_title, description: f.value_1_description },
              { title: f.value_2_title, description: f.value_2_description },
              { title: f.value_3_title, description: f.value_3_description },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card text-center"
              >
                <h3 className="text-lg font-semibold text-its-gold mb-3">{value.title}</h3>
                <p className="text-its-offwhite/80 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional CMS Content */}
      {content && (
        <section className="section-padding">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto prose-content"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }}
            />
          </div>
        </section>
      )}
    </Layout>
  );
};

export default AboutPage;
